import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { type UseFormReturn, useForm } from 'react-hook-form';
import { specificClientPriceApi } from '@/api/commercial/specific-client-prices';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  buildSpecificClientPriceRanges,
  makeSpecificClientPriceTiers,
  normalizeSpecificClientPriceTiers,
  type SpecificClientPriceTier,
  specificClientPriceRangeToTier,
  useSpecificClientPriceRanges,
  validateSpecificClientPriceRanges,
} from '@/hooks/commercial/specific-client-price-ranges';
import { useSpecificClientPrice } from '@/hooks/commercial/use-specific-client-price';
import { toast } from '@/hooks/use-toast';
import { centsToPtbr } from '@/lib/formatters';
import {
  type SpecificClientPriceFormData,
  specificClientPriceSchema,
} from '@/schemas/commercial/specific-client-price.schema';
import { NegotiationType } from '@/types/commercial/cart';
import type { CreateSpecificClientPricePayload } from '@/types/commercial/specific-client-prices';

type SpecificPricePaymentType = 'INSTALLMENT' | 'CASH' | 'MONTHLY' | 'YEARLY' | 'FIXED';

interface SavedBlock {
  tiersEnabled: boolean;
  tiers: SpecificClientPriceTier[];
  flatPrice: number | '';
}

interface SpecificPriceInlineFormProps {
  accountId: string | undefined;
  productId: string | undefined;
  negotiationType: NegotiationType | undefined;
  type: 'product' | 'connectivity' | 'monthly' | 'yearly' | 'fixed';
  proposalId?: string;
  fallbackPriceCashInCents?: number;
  fallbackPriceInstallmentInCents?: number;
  fallbackPriceMonthlyInCents?: number;
  fallbackPriceYearlyInCents?: number;
  fallbackPriceFixedInCents?: number;
  onClose: () => void;
}

function centsToReais(cents: number): number {
  return cents / 100;
}

function reaisToCents(value?: number | ''): number {
  if (value === undefined || value === '') return 0;
  return Math.round(value * 100);
}

function fallbackWhenZero(value: number | undefined, fallback: number): number {
  return value && value > 0 ? value : fallback;
}

function buildCreateSpecificClientPriceReference(
  negotiationType: NegotiationType,
  itemId: string,
): Pick<
  CreateSpecificClientPricePayload,
  'productId' | 'accessoryId' | 'connectivityId' | 'serviceId'
> {
  switch (negotiationType) {
    case NegotiationType.SALE_WITH_SIM_CARD:
    case NegotiationType.SALE_WITHOUT_SIM_CARD:
      return { productId: itemId };
    case NegotiationType.ACCESSORY_SALE:
      return { accessoryId: itemId };
    case NegotiationType.SIM_CARD:
      return { connectivityId: itemId };
    case NegotiationType.SERVICES:
      return { serviceId: itemId };
  }
}

function parseOptionalInteger(value: string): number | '' {
  if (value === '') return '';
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? '' : parsed;
}

function hasOpenEndedTo(tier: SpecificClientPriceTier): boolean {
  return tier.to === '';
}

function formatTierQuantity(tier: SpecificClientPriceTier): string {
  return hasOpenEndedTo(tier) ? `A partir de ${tier.from}` : `De ${tier.from} ate ${tier.to}`;
}

function getNextTierFrom(tiers: SpecificClientPriceTier[], index: number): number | null {
  if (index === 0 || tiers[index]?.from !== '') return null;

  const previousTo = tiers[index - 1]?.to;
  return typeof previousTo === 'number' ? previousTo + 1 : null;
}

function TierList({
  tiers,
  onChange,
  onRemove,
  onAdd,
}: {
  tiers: SpecificClientPriceTier[];
  onChange: (index: number, field: 'from' | 'to' | 'price', value: number | '') => void;
  onRemove: (index: number) => void;
  onAdd: () => void;
}) {
  return (
    <div className="space-y-2">
      {tiers.map((tier, i) => {
        const isLast = i === tiers.length - 1;
        const fieldsGridClass = isLast
          ? 'grid min-w-0 flex-1 grid-cols-1 gap-2 sm:grid-cols-2'
          : 'grid min-w-0 flex-1 grid-cols-1 gap-2 sm:grid-cols-3';

        return (
          <div key={tier.id} className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-end">
            <div className={fieldsGridClass}>
              <div className="min-w-0 space-y-1">
                <FormLabel className="text-xs text-muted-foreground">
                  {isLast ? 'A partir de' : 'De'}
                </FormLabel>
                <Input
                  className="w-full min-w-0"
                  type="number"
                  min={1}
                  placeholder="0"
                  value={tier.from}
                  disabled={i === 0}
                  onFocus={() => {
                    const nextFrom = getNextTierFrom(tiers, i);
                    if (nextFrom !== null) onChange(i, 'from', nextFrom);
                  }}
                  onChange={(e) => {
                    onChange(i, 'from', parseOptionalInteger(e.target.value));
                  }}
                />
              </div>
              {!isLast && (
                <div className="min-w-0 space-y-1">
                  <FormLabel className="text-xs text-muted-foreground">Até</FormLabel>
                  <Input
                    className="w-full min-w-0"
                    type="number"
                    min={1}
                    placeholder="0"
                    value={tier.to}
                    onChange={(e) => {
                      onChange(i, 'to', parseOptionalInteger(e.target.value));
                    }}
                  />
                </div>
              )}
              <div className="min-w-0 space-y-1">
                <FormLabel className="whitespace-nowrap text-xs text-muted-foreground">
                  Preço un. (R$)
                </FormLabel>
                <Input
                  className="w-full min-w-0"
                  type="number"
                  min={0}
                  step="0.01"
                  placeholder="0,00"
                  value={tier.price}
                  onChange={(e) => {
                    const v = e.target.value;
                    onChange(i, 'price', v === '' ? '' : Number(v));
                  }}
                />
              </div>
            </div>
            {tiers.length > 1 && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-9 w-9 shrink-0 self-end text-red-500 hover:text-red-700"
                onClick={() => onRemove(i)}
                disabled={tiers.length <= 2}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        );
      })}
      <Button type="button" variant="outline" size="sm" className="mt-1 max-w-full" onClick={onAdd}>
        <Plus className="mr-1 h-3.5 w-3.5" />
        Adicionar faixa
      </Button>
    </div>
  );
}

function PaymentBlock({
  label,
  priceFieldName,
  form,
  tiersEnabled,
  onToggleTiers,
  tiers,
  onTierChange,
  onTierRemove,
  onTierAdd,
  savedData,
  allowTiers = true,
}: {
  label: string;
  priceFieldName: 'priceCash' | 'priceInstallment' | 'priceMonthly' | 'priceYearly' | 'priceFixed';
  form: UseFormReturn<SpecificClientPriceFormData>;
  tiersEnabled: boolean;
  onToggleTiers: (enabled: boolean) => void;
  tiers: SpecificClientPriceTier[];
  onTierChange: (index: number, field: 'from' | 'to' | 'price', value: number | '') => void;
  onTierRemove: (index: number) => void;
  onTierAdd: () => void;
  savedData: SavedBlock | null;
  allowTiers?: boolean;
}) {
  if (savedData) {
    return (
      <div className="min-w-0 space-y-2 rounded-md border border-gray-200 bg-white p-3">
        <p className="text-sm font-semibold">{label}</p>
        {savedData.tiersEnabled ? (
          <div className="space-y-1">
            {savedData.tiers.map((tier) => (
              <p key={tier.id} className="text-sm text-gray-600">
                {formatTierQuantity(tier)}&nbsp;&nbsp;&nbsp;R${' '}
                {centsToPtbr(reaisToCents(tier.price))}
              </p>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-600">
            Preço por unidade: R$ {centsToPtbr(reaisToCents(savedData.flatPrice))}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="min-w-0 space-y-3 rounded-md border border-gray-200 bg-white p-3">
      <p className="text-sm font-semibold">{label}</p>

      {allowTiers && (
        <div className="flex items-center gap-2">
          <Switch checked={tiersEnabled} onCheckedChange={onToggleTiers} />
          <span className="text-xs text-muted-foreground">Faixas de quantidade</span>
        </div>
      )}

      {allowTiers && tiersEnabled ? (
        <TierList tiers={tiers} onChange={onTierChange} onRemove={onTierRemove} onAdd={onTierAdd} />
      ) : (
        <FormField
          control={form.control}
          name={priceFieldName}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-xs text-muted-foreground">
                Preço por unidade (R$)
              </FormLabel>
              <FormControl>
                <Input
                  className="w-full min-w-0"
                  type="number"
                  min={0}
                  step="0.01"
                  placeholder="0,00"
                  {...field}
                  value={field.value ?? ''}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </div>
  );
}

export function SpecificPriceInlineForm({
  accountId,
  productId,
  negotiationType,
  type,
  proposalId,
  fallbackPriceCashInCents = 0,
  fallbackPriceInstallmentInCents = 0,
  fallbackPriceMonthlyInCents = 0,
  fallbackPriceYearlyInCents = 0,
  fallbackPriceFixedInCents = 0,
  onClose,
}: SpecificPriceInlineFormProps) {
  const isProductPrice = type === 'product';
  const isMonthlyPrice = type === 'connectivity' || type === 'monthly';
  const isYearlyPrice = type === 'yearly';
  const isFixedPrice = type === 'fixed';
  const queryClient = useQueryClient();

  const { existingPrice, isLoading, save, isSaving } = useSpecificClientPrice(
    accountId,
    productId,
    negotiationType,
    proposalId,
  );

  const form = useForm<SpecificClientPriceFormData>({
    resolver: zodResolver(specificClientPriceSchema),
    defaultValues: {
      priceCash: undefined,
      priceInstallment: undefined,
      priceMonthly: undefined,
      priceYearly: undefined,
      priceFixed: undefined,
    },
  });

  const { reset } = form;
  const supportsRangePricing =
    isProductPrice &&
    negotiationType !== NegotiationType.SIM_CARD &&
    negotiationType !== NegotiationType.SERVICES;

  const [installmentTiersEnabled, setInstallmentTiersEnabled] = useState(false);
  const {
    tiers: installmentTiers,
    replaceTiers: replaceInstallmentTiers,
    addTier: addInstallmentTier,
    updateTier: updateInstallmentTier,
    removeTier: removeInstallmentTier,
  } = useSpecificClientPriceRanges();
  const [cashTiersEnabled, setCashTiersEnabled] = useState(false);
  const {
    tiers: cashTiers,
    replaceTiers: replaceCashTiers,
    addTier: addCashTier,
    updateTier: updateCashTier,
    removeTier: removeCashTier,
  } = useSpecificClientPriceRanges();
  const [savedInstallment, setSavedInstallment] = useState<SavedBlock | null>(null);
  const [savedCash, setSavedCash] = useState<SavedBlock | null>(null);
  const [savedMonthly, setSavedMonthly] = useState<SavedBlock | null>(null);
  const [savedYearly, setSavedYearly] = useState<SavedBlock | null>(null);
  const [savedFixed, setSavedFixed] = useState<SavedBlock | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: populate form when data arrives
  useEffect(() => {
    if (existingPrice) {
      const cashRanges =
        existingPrice.ranges?.filter((range) => range.paymentType === 'CASH') ?? [];
      const installmentRanges =
        existingPrice.ranges?.filter(
          (range) => range.paymentType === 'INSTALLMENT' || range.paymentType === 'INSTALLMENTS',
        ) ?? [];

      reset({
        priceCash: existingPrice.priceCashInCents
          ? centsToReais(existingPrice.priceCashInCents)
          : undefined,
        priceInstallment: existingPrice.priceInstallmentInCents
          ? centsToReais(existingPrice.priceInstallmentInCents)
          : undefined,
        priceMonthly: existingPrice.priceMonthlyInCents
          ? centsToReais(existingPrice.priceMonthlyInCents)
          : undefined,
        priceYearly: existingPrice.priceYearlyInCents
          ? centsToReais(existingPrice.priceYearlyInCents)
          : undefined,
        priceFixed: existingPrice.priceFixedInCents
          ? centsToReais(existingPrice.priceFixedInCents)
          : undefined,
      });

      setCashTiersEnabled(supportsRangePricing && cashRanges.length > 0);
      replaceCashTiers(
        supportsRangePricing && cashRanges.length > 0
          ? cashRanges.map(specificClientPriceRangeToTier)
          : makeSpecificClientPriceTiers(),
      );
      setInstallmentTiersEnabled(supportsRangePricing && installmentRanges.length > 0);
      replaceInstallmentTiers(
        supportsRangePricing && installmentRanges.length > 0
          ? installmentRanges.map(specificClientPriceRangeToTier)
          : makeSpecificClientPriceTiers(),
      );
      setSavedCash(
        cashRanges.length > 0 || existingPrice.priceCashInCents > 0
          ? {
              tiersEnabled: supportsRangePricing && cashRanges.length > 0,
              tiers: supportsRangePricing
                ? normalizeSpecificClientPriceTiers(cashRanges.map(specificClientPriceRangeToTier))
                : [],
              flatPrice: existingPrice.priceCashInCents
                ? centsToReais(existingPrice.priceCashInCents)
                : '',
            }
          : null,
      );
      setSavedInstallment(
        installmentRanges.length > 0 || existingPrice.priceInstallmentInCents > 0
          ? {
              tiersEnabled: supportsRangePricing && installmentRanges.length > 0,
              tiers: supportsRangePricing
                ? normalizeSpecificClientPriceTiers(
                    installmentRanges.map(specificClientPriceRangeToTier),
                  )
                : [],
              flatPrice: existingPrice.priceInstallmentInCents
                ? centsToReais(existingPrice.priceInstallmentInCents)
                : '',
            }
          : null,
      );
      setSavedMonthly(
        isMonthlyPrice && existingPrice.priceMonthlyInCents > 0
          ? {
              tiersEnabled: false,
              tiers: [],
              flatPrice: centsToReais(existingPrice.priceMonthlyInCents),
            }
          : null,
      );
      setSavedYearly(
        isYearlyPrice && existingPrice.priceYearlyInCents > 0
          ? {
              tiersEnabled: false,
              tiers: [],
              flatPrice: centsToReais(existingPrice.priceYearlyInCents),
            }
          : null,
      );
      setSavedFixed(
        isFixedPrice && existingPrice.priceFixedInCents > 0
          ? {
              tiersEnabled: false,
              tiers: [],
              flatPrice: centsToReais(existingPrice.priceFixedInCents),
            }
          : null,
      );
    }
  }, [existingPrice]);

  const handleSave = form.handleSubmit(async (data) => {
    const installmentCents = isProductPrice
      ? reaisToCents(data.priceInstallment)
      : fallbackWhenZero(existingPrice?.priceInstallmentInCents, fallbackPriceInstallmentInCents);
    const cashCents = isProductPrice
      ? reaisToCents(data.priceCash)
      : fallbackWhenZero(existingPrice?.priceCashInCents, fallbackPriceCashInCents);
    const monthlyCents = isMonthlyPrice
      ? reaisToCents(data.priceMonthly)
      : fallbackWhenZero(existingPrice?.priceMonthlyInCents, fallbackPriceMonthlyInCents);
    const yearlyCents = isYearlyPrice
      ? reaisToCents(data.priceYearly)
      : fallbackWhenZero(existingPrice?.priceYearlyInCents, fallbackPriceYearlyInCents);
    const fixedCents = isFixedPrice
      ? reaisToCents(data.priceFixed)
      : fallbackWhenZero(existingPrice?.priceFixedInCents, fallbackPriceFixedInCents);
    const ranges = supportsRangePricing
      ? [
          ...buildSpecificClientPriceRanges(
            'INSTALLMENT',
            installmentTiersEnabled,
            installmentTiers,
            reaisToCents,
          ),
          ...buildSpecificClientPriceRanges('CASH', cashTiersEnabled, cashTiers, reaisToCents),
        ]
      : [];

    const rangeError = validateSpecificClientPriceRanges(ranges);
    if (rangeError) {
      toast({ title: rangeError, variant: 'destructive' });
      return;
    }

    const hasInstallmentRanges = ranges.some((range) => range.paymentType === 'INSTALLMENT');
    const hasCashRanges = ranges.some((range) => range.paymentType === 'CASH');

    const shouldSaveInstallment =
      isProductPrice && !savedInstallment && (installmentCents > 0 || hasInstallmentRanges);
    const shouldSaveCash = isProductPrice && !savedCash && (cashCents > 0 || hasCashRanges);
    const shouldSaveMonthly = isMonthlyPrice && !savedMonthly && monthlyCents > 0;
    const shouldSaveYearly = isYearlyPrice && !savedYearly && yearlyCents > 0;
    const shouldSaveFixed = isFixedPrice && !savedFixed && fixedCents > 0;

    const hasAnyPrice =
      shouldSaveInstallment ||
      shouldSaveCash ||
      shouldSaveMonthly ||
      shouldSaveYearly ||
      shouldSaveFixed;

    if (!hasAnyPrice && !existingPrice) {
      toast({ title: 'Preencha ao menos um preço', variant: 'destructive' });
      return;
    }

    try {
      if (!hasAnyPrice && existingPrice) {
        await specificClientPriceApi.remove(existingPrice.id, proposalId);
        await queryClient.invalidateQueries({
          queryKey: ['specific-client-price', accountId, productId, negotiationType],
        });
        if (proposalId) {
          await queryClient.invalidateQueries({ queryKey: ['cart-calculate', proposalId] });
          await queryClient.invalidateQueries({ queryKey: ['cart-items', proposalId] });
        }
        clearAllPrices();
        toast({ title: 'Preço específico removido com sucesso' });
        return;
      }

      const payload = {
        priceCashInCents: cashCents,
        priceInstallmentInCents: installmentCents,
        priceMonthlyInCents: monthlyCents,
        priceYearlyInCents: yearlyCents,
        priceFixedInCents: fixedCents,
        ranges,
        proposalId,
      };

      if (existingPrice) {
        const result = await save({
          mode: 'update',
          id: existingPrice.id,
          data: payload,
        });
        if (!result.success) throw new Error(result.error?.global ?? 'Erro ao salvar preço');
      } else {
        const result = await save({
          mode: 'create',
          data: {
            accountId: accountId!,
            negotiationType: negotiationType!,
            ...buildCreateSpecificClientPriceReference(negotiationType!, productId!),
            ...payload,
          },
        });
        if (!result.success) throw new Error(result.error?.global ?? 'Erro ao salvar preço');
      }

      if (shouldSaveInstallment) {
        setSavedInstallment({
          tiersEnabled: installmentTiersEnabled,
          tiers: installmentTiers.filter((t) => t.price !== ''),
          flatPrice: data.priceInstallment ?? '',
        });
      }
      if (shouldSaveCash) {
        setSavedCash({
          tiersEnabled: cashTiersEnabled,
          tiers: cashTiers.filter((t) => t.price !== ''),
          flatPrice: data.priceCash ?? '',
        });
      }
      if (shouldSaveMonthly) {
        setSavedMonthly({
          tiersEnabled: false,
          tiers: [],
          flatPrice: data.priceMonthly ?? '',
        });
      }
      if (shouldSaveYearly) {
        setSavedYearly({
          tiersEnabled: false,
          tiers: [],
          flatPrice: data.priceYearly ?? '',
        });
      }
      if (shouldSaveFixed) {
        setSavedFixed({
          tiersEnabled: false,
          tiers: [],
          flatPrice: data.priceFixed ?? '',
        });
      }

      toast({ title: 'Preço específico salvo com sucesso' });
    } catch {
      toast({ title: 'Erro ao salvar preço específico', variant: 'destructive' });
    }
  });

  const handleUpdatePrice = (paymentType: SpecificPricePaymentType) => {
    if (paymentType === 'INSTALLMENT') {
      setSavedInstallment(null);
      return;
    }

    if (paymentType === 'CASH') {
      setSavedCash(null);
      return;
    }

    if (paymentType === 'MONTHLY') {
      setSavedMonthly(null);
      return;
    }

    if (paymentType === 'YEARLY') {
      setSavedYearly(null);
      return;
    }

    setSavedFixed(null);
  };

  const handleDeletePrice = (paymentType: SpecificPricePaymentType) => {
    if (paymentType === 'INSTALLMENT') {
      setSavedInstallment(null);
      replaceInstallmentTiers(makeSpecificClientPriceTiers());
      setInstallmentTiersEnabled(false);
      form.setValue('priceInstallment', undefined);
      return;
    }

    if (paymentType === 'CASH') {
      setSavedCash(null);
      replaceCashTiers(makeSpecificClientPriceTiers());
      setCashTiersEnabled(false);
      form.setValue('priceCash', undefined);
      return;
    }

    if (paymentType === 'MONTHLY') {
      setSavedMonthly(null);
      form.setValue('priceMonthly', undefined);
      return;
    }

    if (paymentType === 'YEARLY') {
      setSavedYearly(null);
      form.setValue('priceYearly', undefined);
      return;
    }

    setSavedFixed(null);
    form.setValue('priceFixed', undefined);
  };

  const handleEditAll = () => {
    handleUpdatePrice('INSTALLMENT');
    handleUpdatePrice('CASH');

    if (isMonthlyPrice) {
      handleUpdatePrice('MONTHLY');
    }
    if (isYearlyPrice) handleUpdatePrice('YEARLY');
    if (isFixedPrice) handleUpdatePrice('FIXED');
  };

  const clearAllPrices = () => {
    handleDeletePrice('INSTALLMENT');
    handleDeletePrice('CASH');

    if (isMonthlyPrice) {
      handleDeletePrice('MONTHLY');
    }
    if (isYearlyPrice) handleDeletePrice('YEARLY');
    if (isFixedPrice) handleDeletePrice('FIXED');
  };

  const handleDeleteAll = async () => {
    try {
      setIsDeleting(true);

      if (existingPrice) {
        await specificClientPriceApi.remove(existingPrice.id, proposalId);
        await queryClient.invalidateQueries({
          queryKey: ['specific-client-price', accountId, productId, negotiationType],
        });
        if (proposalId) {
          await queryClient.invalidateQueries({ queryKey: ['cart-calculate', proposalId] });
          await queryClient.invalidateQueries({ queryKey: ['cart-items', proposalId] });
        }
      }

      clearAllPrices();
      toast({ title: 'Preço específico removido com sucesso' });
    } catch {
      toast({ title: 'Erro ao remover preço específico', variant: 'destructive' });
    } finally {
      setIsDeleting(false);
    }
  };

  const hasUnsavedBlocks =
    (isProductPrice && (!savedInstallment || !savedCash)) ||
    (isMonthlyPrice && !savedMonthly) ||
    (isYearlyPrice && !savedYearly) ||
    (isFixedPrice && !savedFixed);
  const hasSavedBlocks = Boolean(
    (isProductPrice && (savedInstallment || savedCash)) ||
      (isMonthlyPrice && savedMonthly) ||
      (isYearlyPrice && savedYearly) ||
      (isFixedPrice && savedFixed),
  );

  if (isLoading) {
    return (
      <div className="rounded-md border border-gray-200 bg-gray-50 p-4">
        <p className="text-sm text-muted-foreground">Carregando preço específico...</p>
      </div>
    );
  }

  return (
    <div className="min-w-0 space-y-4 overflow-x-hidden rounded-md border border-gray-200 bg-gray-50 p-4">
      <div className="flex min-w-0 items-center justify-between gap-3">
        <p className="text-sm font-medium text-gray-700">Preços específicos do cliente</p>

        {hasSavedBlocks && (
          <div className="flex shrink-0 items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-red-500 hover:text-red-700"
              onClick={handleDeleteAll}
              disabled={isDeleting || isSaving}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={handleEditAll}
              disabled={isDeleting || isSaving}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </div>

      <Form {...form}>
        <div className="space-y-4">
          {isProductPrice && (
            <div className="grid min-w-0 grid-cols-1 gap-3 md:grid-cols-2">
              <PaymentBlock
                label="Pagamento a prazo"
                priceFieldName="priceInstallment"
                form={form}
                tiersEnabled={installmentTiersEnabled}
                onToggleTiers={setInstallmentTiersEnabled}
                tiers={installmentTiers}
                onTierChange={(i, f, v) => updateInstallmentTier(i, { [f]: v })}
                onTierRemove={removeInstallmentTier}
                onTierAdd={addInstallmentTier}
                savedData={savedInstallment}
                allowTiers={supportsRangePricing}
              />
              <PaymentBlock
                label="Pagamento à vista"
                priceFieldName="priceCash"
                form={form}
                tiersEnabled={cashTiersEnabled}
                onToggleTiers={setCashTiersEnabled}
                tiers={cashTiers}
                onTierChange={(i, f, v) => updateCashTier(i, { [f]: v })}
                onTierRemove={removeCashTier}
                onTierAdd={addCashTier}
                savedData={savedCash}
                allowTiers={supportsRangePricing}
              />
            </div>
          )}

          {isMonthlyPrice && (
            <PaymentBlock
              label="Pagamento mensal"
              priceFieldName="priceMonthly"
              form={form}
              tiersEnabled={false}
              onToggleTiers={() => undefined}
              tiers={[]}
              onTierChange={() => undefined}
              onTierRemove={() => undefined}
              onTierAdd={() => undefined}
              savedData={savedMonthly}
              allowTiers={false}
            />
          )}

          {isYearlyPrice && (
            <PaymentBlock
              label="Pagamento anual"
              priceFieldName="priceYearly"
              form={form}
              tiersEnabled={false}
              onToggleTiers={() => undefined}
              tiers={[]}
              onTierChange={() => undefined}
              onTierRemove={() => undefined}
              onTierAdd={() => undefined}
              savedData={savedYearly}
              allowTiers={false}
            />
          )}

          {isFixedPrice && (
            <PaymentBlock
              label="Pagamento fixo"
              priceFieldName="priceFixed"
              form={form}
              tiersEnabled={false}
              onToggleTiers={() => undefined}
              tiers={[]}
              onTierChange={() => undefined}
              onTierRemove={() => undefined}
              onTierAdd={() => undefined}
              savedData={savedFixed}
              allowTiers={false}
            />
          )}

          {hasUnsavedBlocks ? (
            <div className="flex flex-wrap justify-end gap-2">
              <Button type="button" variant="outline" size="sm" onClick={onClose}>
                Cancelar
              </Button>
              <Button
                type="button"
                size="sm"
                className="bg-black text-white hover:bg-gray-800"
                onClick={handleSave}
                disabled={isSaving || isDeleting}
              >
                {isSaving ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          ) : (
            <div className="flex justify-end">
              <Button type="button" variant="outline" size="sm" onClick={onClose}>
                Fechar
              </Button>
            </div>
          )}
        </div>
      </Form>
    </div>
  );
}
